import { Network } from '@capacitor/network';
import { isNative } from './capacitorService';

const PROBE_URL = `${process.env.REACT_APP_API_BASE_URL || 'https://apiteratasks.teralintas.com/api'}/health`;
const PROBE_TIMEOUT_MS = 5000;
// Selama status offline, coba lagi berkala. Tanpa ini, status hanya pernah dievaluasi ulang saat
// ada event online/offline BARU dari browser/OS — kalau 1x probe gagal karena sebab sesaat (DNS,
// firewall/AV laptop yang kebetulan block domain API tapi situs lain normal, WiFi masih
// reconnect saat app baru dibuka, dll), badge "Offline Mode" bisa macet SELAMANYA di laptop yang
// koneksinya stabil (tidak pernah toggle WiFi off/on lagi untuk memicu event baru).
const RETRY_INTERVAL_MS = 15000;

// Cek koneksi nyata via probe request — navigator.onLine tidak reliable
export async function checkRealConnectivity(): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
    const res = await fetch(PROBE_URL, {
      method: 'HEAD',
      cache: 'no-store',
      signal: controller.signal,
    });
    clearTimeout(timer);
    return res.ok || res.status < 500;
  } catch {
    return false;
  }
}

type NetworkCallback = (isOnline: boolean) => void;

let _callback: NetworkCallback | null = null;
let _retryTimer: ReturnType<typeof setInterval> | null = null;
let _onlineListener: (() => void) | null = null;
let _offlineListener: (() => void) | null = null;

function stopRetryPolling() {
  if (_retryTimer !== null) {
    clearInterval(_retryTimer);
    _retryTimer = null;
  }
}

function startRetryPolling() {
  if (_retryTimer !== null) return; // sudah jalan, jangan dobel
  _retryTimer = setInterval(async () => {
    const real = await checkRealConnectivity();
    if (real) {
      stopRetryPolling();
      _callback?.(true);
    }
  }, RETRY_INTERVAL_MS);
}

async function handleStatusChange(connected: boolean) {
  if (!connected) {
    startRetryPolling();
    _callback?.(false);
    return;
  }
  // Double-check dengan probe sebelum declare online
  const real = await checkRealConnectivity();
  if (real) {
    stopRetryPolling();
  } else {
    // OS/browser bilang "connected", tapi probe ke backend gagal — tetap anggap offline dan
    // mulai polling retry, supaya begitu endpoint beneran bisa dihubungi lagi, badge otomatis
    // hilang sendiri tanpa perlu user toggle WiFi.
    startRetryPolling();
  }
  _callback?.(real);
}

// Mulai listener — panggil sekali saat app mount
export async function startNetworkListener(callback: NetworkCallback): Promise<void> {
  _callback = callback;

  if (isNative) {
    // Capacitor Network plugin — akurat di Android/iOS
    Network.addListener('networkStatusChange', (status) => {
      handleStatusChange(status.connected);
    });
    // Cek status awal
    const status = await Network.getStatus();
    handleStatusChange(status.connected);
  } else {
    // Browser: event online/offline + probe. Simpan referensi listener-nya (bukan arrow function
    // baru tiap kali) supaya stopNetworkListener() beneran bisa me-remove-nya — removeEventListener
    // silent no-op kalau referensi fungsinya tidak persis sama dengan yang didaftarkan.
    _onlineListener = () => handleStatusChange(true);
    _offlineListener = () => handleStatusChange(false);
    window.addEventListener('online', _onlineListener);
    window.addEventListener('offline', _offlineListener);
    // Status awal
    handleStatusChange(navigator.onLine);
  }
}

export async function stopNetworkListener(): Promise<void> {
  stopRetryPolling();
  if (isNative) {
    await Network.removeAllListeners();
  } else {
    if (_onlineListener) window.removeEventListener('online', _onlineListener);
    if (_offlineListener) window.removeEventListener('offline', _offlineListener);
    _onlineListener = null;
    _offlineListener = null;
  }
  _callback = null;
}
